/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import {
  App,
  Button as ButtonAnt,
  Checkbox,
  Col,
  Form,
  FormProps,
  Input,
  Popover,
  Row,
  TableColumnsType,
  Typography
} from 'antd';
import { useQuery } from '@/hooks/query.hook';
import { PageURLs } from '@/utils/navigate';
import { Icon } from '../icons';
import { Button } from '../button';
import { useEffect, useMemo } from 'react';
import { Table } from '../tables/table';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
  fetchPermissionFields,
  fetchPermissionModules,
  watchPermissionFields,
  watchPermissionModules
} from '@/redux/slices/permissions.slice';
import {
  createRole,
  resetMessages,
  watchRoleMessage
} from '@/redux/slices/roles.slice';
import { setMessages } from '@/redux/slices/app.slice';
import { Message } from '@/enums/message.enum';
import { PERMISSION } from '@/constants/permissions.constant';
import { CaretDownOutlined, CaretUpOutlined } from '@ant-design/icons';
import { useBeforeUnload } from 'react-router-dom';

type FieldType = {
  name: string;
  desc: string;
  permissions: Record<string, boolean>;
};

export const RoleCreateForm = () => {
  const { push } = useQuery();
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const { modal } = App.useApp();
  const permissionFields = useAppSelector(watchPermissionFields);
  const permissionModules = useAppSelector(watchPermissionModules);
  const messageInfo = useAppSelector(watchRoleMessage);

  const permissionsData = useMemo(
    () =>
      permissionModules.map((module) => ({
        index: `${module.index}`,
        name: module.name,
        children: module.childrens.map((child: PermissionChild) => ({
          index: `${module.index}-${child.index}`,
          name: child.name,
          permissionRelation: child.permissionRelation
        }))
      })),
    [permissionModules]
  );

  const childIndices = useMemo(() => {
    const indices: string[] = [];
    permissionsData.forEach((module) => {
      module.children.forEach((child: PermissionChild) => {
        indices.push(child.index);
      });
    });
    return new Set(indices);
  }, [permissionsData]);

  const handleGoBack = () => {
    push(PageURLs.ofRoleManagement());
  };

  const onFinish: FormProps<FieldType>['onFinish'] = async (values) => {
    const modulePermission = Object.entries(values.permissions)
      .filter(([_, value]) => value)
      .map(([key]) => {
        const values = key.split('-');
        return {
          moduleIndex: Number(values[1]),
          permissionIndex: Number(values[2])
        };
      });

    const modulePermissionJson = JSON.stringify(modulePermission);

    if (!modulePermissionJson || modulePermissionJson.length < 4) {
      alertSelectPermission();
      return;
    }

    const payload = {
      name: values.name,
      desc: values.desc ?? '',
      modulePermission
    };

    const result = await dispatch(createRole(payload));

    if (createRole.fulfilled.match(result)) {
      handleGoBack();
    }
  };

  const alertSelectPermission = () => {
    modal.confirm({
      title: 'Thông báo',
      content: <em>Phải có ít nhất 1 chức năng được phân quyền</em>,
      okText: 'Xác nhận',
      width: 450,
      cancelText: 'Hủy'
    });
  };

  const columns: TableColumnsType<PermissionChild> = [
    {
      title: 'Danh sách chức năng (Tên vai trò)',
      dataIndex: 'name',
      key: 'name'
    },
    ...permissionFields.map((col) => ({
      title: col.name,
      dataIndex: col.index,
      key: col.index,
      render: (_: string, record: PermissionChild) => {
        const isChild = childIndices.has(record.index);

        if (isChild) {
          const hasPermission = record.permissionRelation.includes(col.index);
          if (!hasPermission) return null;

          const permissionKey =
            `${record.index}-${col.index}` as keyof typeof PERMISSION;
          return (
            <Popover placement='topLeft' content={PERMISSION[permissionKey]}>
              <Form.Item<FieldType>
                name={['permissions', `${record.index}-${col.index}`]}
                valuePropName='checked'
                initialValue={false}
                noStyle
              >
                <Checkbox />
              </Form.Item>
            </Popover>
          );
        }

        return null;
      }
    }))
  ];

  useEffect(() => {
    dispatch(fetchPermissionFields());
  }, [dispatch, form]);

  useEffect(() => {
    dispatch(fetchPermissionModules());
  }, [dispatch]);

  useEffect(() => {
    if (messageInfo) {
      dispatch(
        setMessages([
          {
            type: messageInfo.type,
            message: messageInfo.message,
            description: messageInfo.description
          }
        ])
      );
      if (messageInfo.type === Message.SUCCESS) {
        form.resetFields();
      }
      dispatch(resetMessages());
    }
  }, [dispatch, messageInfo, form]);

  useBeforeUnload((e) => {
    e.preventDefault();
  });

  return (
    <div css={rootStyles}>
      <Typography.Title css={titleStyles} level={5}>
        <ButtonAnt
          css={goBackBtnStyles}
          onClick={handleGoBack}
          type='text'
          icon={<Icon type='arrowLeft' width={20} height={20} />}
        />
        Thêm mới vai trò
      </Typography.Title>
      <Form
        form={form}
        name='create-admin'
        css={formStyles}
        initialValues={{ remember: true }}
        onFinish={onFinish}
        autoComplete='off'
        className='ant-form-custom'
        requiredMark={false}
      >
        <Form.Item<FieldType>
          label='Tên vai trò (*)'
          name='name'
          rules={[{ required: true, message: 'Tên vai trò là bắt buộc' }]}
        >
          <Input size='large' placeholder='Nhập tên vai trò' />
        </Form.Item>

        <Form.Item<FieldType> label='Mô tả' name='desc'>
          <Input.TextArea rows={1} size='large' placeholder='Nhập mô tả' />
        </Form.Item>
        {permissionsData.length > 0 && (
          <Table
            rowKey={(record) => record.index}
            columns={columns as any}
            dataSource={permissionsData}
            expandable={{
              defaultExpandAllRows: true,
              expandIcon: ({ expanded, onExpand, record }) => (
                <Button
                  type='text'
                  style={{
                    background: 'none',
                    border: 'none',
                    boxShadow: 'none'
                  }}
                  onClick={(e) => onExpand(record, e)}
                  icon={expanded ? <CaretDownOutlined /> : <CaretUpOutlined />}
                />
              )
            }}
            pagination={false}
          />
        )}
        <Row css={footerStyles} justify='center' gutter={[20, 0]}>
          <Col>
            <Button onClick={handleGoBack} css={actionBtnStyles} shape='round'>
              Huỷ bỏ
            </Button>
          </Col>
          <Col>
            <Button
              css={actionBtnStyles}
              htmlType='submit'
              type='primary'
              shape='round'
            >
              Lưu
            </Button>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

const rootStyles = css`
  display: flex;
  flex-direction: column;
  gap: 1.4rem;
  padding: 1.4rem 2.4rem 2.4rem;
`;

const formStyles = css`
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  .ant-form-item {
    .ant-form-item-label {
      width: 12rem;
      text-align: left;
      font-weight: 500;
      line-height: 4rem;
    }
  }
`;

const titleStyles = css`
  display: flex;
  gap: 0.6rem;
  align-items: center;
  position: relative;
  left: 2rem;
  margin-bottom: 0 !important;
`;

const goBackBtnStyles = css`
  position: absolute;
  left: -3rem;
  width: 2.6rem !important;
  height: 2.6rem !important;
  padding: 0;
  svg {
    margin-top: 0.1rem;
  }
  &:hover {
    border-radius: 50%;
  }
`;

const actionBtnStyles = css`
  width: 10rem;
  font-weight: 500;
`;

const footerStyles = css`
  margin-top: 2rem;
`;
